import { useEffect, useState } from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { ScaffolderField } from '@backstage/plugin-scaffolder-react/alpha';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

type OwnerUiOptions = {
  catalogFilter?: { kind?: string[] };
};

/**
 * A minimal owner autocomplete (kept simple on purpose - Backstage's real
 * OwnerPicker/EntityPicker components aren't part of the package's public
 * API in a directly renderable form, only as legacy-system extensions).
 * Pre-fills with the signed-in user's entity ref on first render; still
 * fully editable, and any typed value is accepted (not just catalog matches).
 */
export const CurrentUserOwnerPickerField = (
  props: FieldExtensionComponentProps<string, OwnerUiOptions>,
) => {
  const { formData, onChange, required, rawErrors, schema, uiSchema } = props;
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const kinds = uiSchema?.['ui:options']?.catalogFilter?.kind ?? [
    'Group',
    'User',
  ];
  const [options, setOptions] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    catalogApi
      .getEntities({
        filter: { kind: kinds },
        fields: ['kind', 'metadata.name', 'metadata.namespace'],
      })
      .then(({ items }) => {
        if (!cancelled) setOptions(items.map(stringifyEntityRef));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogApi]);

  useEffect(() => {
    if (formData) return;
    identityApi
      .getBackstageIdentity()
      .then(({ userEntityRef }) => onChange(userEntityRef));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ScaffolderField
      rawDescription={schema.description}
      required={required}
      rawErrors={rawErrors}
    >
      <Autocomplete
        freeSolo
        options={options}
        value={formData ?? ''}
        onChange={(_, newValue) => onChange(newValue ?? '')}
        onInputChange={(_, newInputValue) => onChange(newInputValue)}
        renderInput={params => (
          <TextField
            {...params}
            label={schema.title}
            required={required}
            error={(rawErrors?.length ?? 0) > 0}
          />
        )}
      />
    </ScaffolderField>
  );
};
